import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsDashboardActions} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {useWritableContext} from 'insights/WritableContext';
import Breadcrumbs from 'nav/Breadcrumbs';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import AnalyticsDashboardActionButtons from './AnalyticsDashboardActionButtons';
import {useAnalyticsDashboardContext} from './AnalyticsDashboardDetailsContext';
import AnalyticsDashboardReadOnlyInfo from './AnalyticsDashboardReadOnlyInfo';
import EditableAnalyticsDashboardName from './EditableAnalyticsDashboardName';
import {GetAnalyticsDashboard} from './graphql/__generated/GetAnalyticsDashboard';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

interface UnconnectedActions {
  updateParameterConfigsAndValues: (
    newParameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
    newParameterValues: Array<AnalyticsParameterValue>
  ) => void;
  updateParameterValues: (newParameterValues: Array<AnalyticsParameterValue>) => void;
}

export interface AnalyticsDashboardEditorHeaderProps {
  actions: Pick<AnalyticsDashboardActions, 'updateAnalyticsDashboard'> & UnconnectedActions;
  analyticsDashboard: AnalyticsDashboard;
}

export default function AnalyticsDashboardEditorHeader(
  props: AnalyticsDashboardEditorHeaderProps
): JSX.Element {
  const writable = useWritableContext();
  const {showReadOnlyBadge} = useAnalyticsDashboardContext();
  const {updateParameterConfigsAndValues, updateParameterValues, ...actions} = props.actions;

  const crumbs = [
    <EditableAnalyticsDashboardName
      key="analyticsDashboard"
      actions={actions}
      analyticsDashboard={props.analyticsDashboard}
    />,
  ];

  return (
    <span className={`u-lr-split ${css(styles.header)}`}>
      <Breadcrumbs crumbs={crumbs} className="breadcrumbs--large" />
      <div className={`u-vertical-center-items ${css(styles.actions)}`}>
        {!writable && showReadOnlyBadge && <AnalyticsDashboardReadOnlyInfo />}
        <AnalyticsDashboardActionButtons
          actions={{
            updateParameterConfigsAndValues,
            updateParameterValues,
          }}
          analyticsDashboard={props.analyticsDashboard}
          className={!writable ? 'u-mini-spacing-left' : undefined}
        />
      </div>
    </span>
  );
}

const styles = StyleSheet.create({
  header: {
    flexWrap: 'wrap',
    gap: '4px 0',
  },
  actions: {
    marginLeft: 'auto',
  },
});
